import type { useGetCarAvailabilityQueryParams } from "@b2bportal/core-cars";
import type {
  GroundSelectionEnum,
  GroundSupplier,
  VehicleAvailability,
} from "@b2bportal/ground-api";
import type { CarLocationSuggestion } from "..";

export type CarAvailabilityQueryParams = ReturnType<
  typeof useGetCarAvailabilityQueryParams
>;

// Define a type for search details
export interface SearchDetails {
  pickupTime: string;
  pickupLocation: CarLocationSuggestion;
  dropoffType: DropoffType;
  dropoffTime: string;
  dropoffLocation: CarLocationSuggestion;
  driverAge: number;
  searchType: GroundSelectionEnum;
  pickupDateTime?: string;
  dropoffDateTime?: string;
}

export const DropoffType = {
  SameLocation: "SameLocation",
  DifferentLocation: "DifferentLocation",
} as const;

export type DropoffType = (typeof DropoffType)[keyof typeof DropoffType];

export interface PriceRange {
  min: number;
  max: number;
}

export const CarTypeFilter = {
  CompactCar: "CompactCar",
  CompactSUV: "CompactSUV",
  Convertible: "Convertible",
  EconomyCar: "EconomyCar",
  EconomySUV: "EconomySUV",
  Electric: "Electric",
  FullSizeCar: "FullSizeCar",
  FullSizeSUV: "FullSizeSUV",
  Hybrid: "Hybrid",
  LuxuryCar: "LuxuryCar",
  LuxurySUV: "LuxurySUV",
  MidSizeCar: "MidSizeCar",
  MidSizeSUV: "MidSizeSUV",
  MiniCar: "MiniCar",
  MinivanVan: "MinivanVan",
  PickupTruck: "PickupTruck",
  PremiumCar: "PremiumCar",
  PremiumSUV: "PremiumSUV",
  RecreationalVehicle: "RecreationalVehicle",
  SportsCar: "SportsCar",
  StandardCar: "StandardCar",
  StandardSUV: "StandardSUV",
  MysteryCar: "MysteryCar",
} as const;

export type CarTypeFilter = keyof typeof CarTypeFilter;

export type Specification = {
  unlimitedMileage: boolean;
  automaticTransmission: boolean;
  airCondition: boolean;
};

export const MinimumBagCount = [0, 2, 3, 4, 5] as const;
export type MinimumBagCount = (typeof MinimumBagCount)[number];

export const PassengerFilter = {
  UpToFive: "UpToFive",
  SixOrMore: "SixOrMore",
} as const;
export type PassengerFilter =
  (typeof PassengerFilter)[keyof typeof PassengerFilter];

export interface FilterState {
  priceRange: PriceRange;
  carType: CarTypeFilter[];
  minimumBags: MinimumBagCount;
  supplier: GroundSupplier[];
  specification: Specification;
  freeCancellation: boolean;
  passenger: PassengerFilter[];
}

export interface FilterOptions {
  priceRange: PriceRange;
}

export enum CarSortOptionEnum {
  Recommended = "recommended",
  Price = "price",
  Distance = "distance",
}

export type CarComparator = (
  a: VehicleAvailability,
  b: VehicleAvailability
) => number;
